import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Controller, useForm } from 'react-hook-form';

import {
  ContactAutoComplete,
  SitesAutoComplete,
  StringMapDropdown,
} from '@/components';
import { StringMapObjectTypeCode } from '@/components/common/string-map-dropdown';
import { useCreateShipmentGroupMasterRestMutation } from '@/services/donor-scheduling';

import {
  ShipmentGroupMasterFormData,
  shipmentGroupMasterSchema,
} from './schema';

export function CreateShipmentGroupMasterDialog({
  visible,
  onHide,
  shipmentGroupId,
  afterSave,
}: {
  visible: boolean;
  onHide: () => void;
  shipmentGroupId: string;
  afterSave: () => void;
}) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ShipmentGroupMasterFormData>({
    resolver: zodResolver(shipmentGroupMasterSchema),
    defaultValues: {
      shipmentGroupId,
      name: '',
      contactId: '',
      billIncubator: false,
      shippingMethod: undefined,
      shipComment: '',
      callTagComment: '',
      billShipping: false,
      siteId: undefined,
      callTagContactId: undefined,
      comment: '',
    },
  });

  const createShipmentGroupMasterRest =
    useCreateShipmentGroupMasterRestMutation();

  const clearForm = () => {
    reset({
      shipmentGroupId,
      name: '',
      contactId: '',
      billIncubator: false,
      shippingMethod: undefined,
      shipComment: '',
      callTagComment: '',
      billShipping: false,
      siteId: undefined,
      callTagContactId: undefined,
      comment: '',
    });
  };

  const onSubmit = async (formData: ShipmentGroupMasterFormData) => {
    try {
      const input = {
        shipmentGroupId: shipmentGroupId,
        name: formData.name,
        contactId: formData.contactId,
        billIncubator: formData.billIncubator,
        shippingMethod: formData.shippingMethod,
        shipComment: formData.shipComment || null,
        callTagComment: formData.callTagComment || null,
        billShipping: formData.billShipping,
        siteId: formData.siteId || null,
        callTagContactId: formData.callTagContactId || null,
        comment: formData.comment || null,
      };

      await createShipmentGroupMasterRest.mutateAsync(input);

      clearForm();
      onHide();
      afterSave();
    } catch (error) {
      console.error('Error creating shipment group master:', error);
    }
  };

  const handleCancel = () => {
    clearForm();
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleCancel}
      header="Add Shipment Group Master"
      footer={
        <>
          <Button label="Cancel" onClick={handleCancel} />
          <Button label="Save" onClick={handleSubmit(onSubmit)} />
        </>
      }
      style={{ width: '600px' }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-medium">Name *</label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  className={errors.name ? 'p-invalid' : ''}
                />
              )}
            />
            {errors.name && (
              <small className="text-red-500">{errors.name.message}</small>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-medium">Contact ID *</label>
            <Controller
              name="contactId"
              control={control}
              render={({ field }) => (
                <ContactAutoComplete
                  value={field.value}
                  onSelect={(e) => field.onChange(e.value?.id)}
                  className={errors.contactId ? 'p-invalid' : ''}
                />
              )}
            />
            {errors.contactId && (
              <small className="text-red-500">{errors.contactId.message}</small>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-medium">Site ID</label>
            <Controller
              name="siteId"
              control={control}
              render={({ field }) => (
                <SitesAutoComplete
                  value={field.value || ''}
                  onSelect={(e) => field.onChange(e.value?.id || undefined)}
                  className={errors.siteId ? 'p-invalid' : ''}
                />
              )}
            />
            {errors.siteId && (
              <small className="text-red-500">{errors.siteId.message}</small>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-medium">Call Tag Contact ID</label>
            <Controller
              name="callTagContactId"
              control={control}
              render={({ field }) => (
                <ContactAutoComplete
                  value={field.value || ''}
                  onSelect={(e) => field.onChange(e.value?.id || undefined)}
                  className={errors.callTagContactId ? 'p-invalid' : ''}
                />
              )}
            />
            {errors.callTagContactId && (
              <small className="text-red-500">
                {errors.callTagContactId.message}
              </small>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Controller
            name="shippingMethod"
            control={control}
            render={({ field }) => (
              <StringMapDropdown
                label={t('shipping.shippingMethod')}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select shipping method"
                className="w-full"
                objectTypeCode={StringMapObjectTypeCode.SHIPPING_METHOD}
                attributeName="tog_shippingmethod"
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <Controller
              name="billIncubator"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.checked)}
                />
              )}
            />
            <label>Bill Incubator</label>
          </div>

          <div className="flex items-center gap-2">
            <Controller
              name="billShipping"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.checked)}
                />
              )}
            />
            <label>Bill Shipping</label>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium">Ship Comment</label>
          <Controller
            name="shipComment"
            control={control}
            render={({ field }) => (
              <InputTextarea
                {...field}
                value={field.value || ''}
                rows={2}
                className="w-full"
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium">Call Tag Comment</label>
          <Controller
            name="callTagComment"
            control={control}
            render={({ field }) => (
              <InputTextarea
                {...field}
                value={field.value || ''}
                rows={2}
                className="w-full"
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium">Comment</label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <InputTextarea
                {...field}
                value={field.value || ''}
                rows={2}
                className="w-full"
              />
            )}
          />
        </div>
      </form>
    </Dialog>
  );
}
